import React from 'react';
import { CircleDashedIcon } from 'lucide-react';
import { ComplexityLevel } from '@/shared/types/api';
import { getComplexityMeta, COMPLEXITY_SEGMENTS } from '@/shared/lib/classificationMeta';
import { cn } from '@/shared/utils/cn';

interface ComplexityBadgeProps {
  value?: ComplexityLevel | string | null;
  isClassifying?: boolean;
}

const Bars: React.FC<{ level: number }> = ({ level }) => (
  <span className="flex items-center gap-0.5" aria-hidden="true">
    {Array.from({ length: COMPLEXITY_SEGMENTS }).map((_, i) => (
      <span
        key={i}
        className={cn('h-2.5 w-1 rounded-[1px] bg-current', i < level ? 'opacity-100' : 'opacity-20')}
      />
    ))}
  </span>
);

/**
 * Complexity as a monochrome segmented bar (1–5). Deliberately NOT color-coded so
 * it never competes with the Impact hue scale. Non-color cue = always-visible label.
 */
export const ComplexityBadge: React.FC<ComplexityBadgeProps> = ({ value, isClassifying }) => {
  if (value == null && isClassifying) {
    return (
      <span
        className="sev sev-none animate-pulse"
        aria-busy="true"
        aria-label="Complexity: classifying"
        title="Complexity classification in progress"
      >
        <Bars level={0} />
        Classifying…
      </span>
    );
  }

  const meta = getComplexityMeta(value);

  if (!meta) {
    return (
      <span className="sev sev-none" aria-label="Complexity: unclassified">
        <CircleDashedIcon className="h-3 w-3" aria-hidden="true" />
        Unclassified
      </span>
    );
  }

  return (
    <span
      className="sev sev-none"
      title={`Complexity: ${meta.label}`}
      aria-label={`Complexity: ${meta.label} (${meta.level} of ${COMPLEXITY_SEGMENTS})`}
    >
      <Bars level={meta.level} />
      {meta.label}
    </span>
  );
};
